import { CheckIcon } from '@icons'
import classNames from 'classnames'
import { Box } from 'grommet'
import { Checkmark } from 'grommet-icons'
import { CSSProperties } from 'react'

export const FormStep = ({
  check,
  step,
  label,
  bg
}: {
  check?: boolean
  step: number
  label: string
  bg?: boolean
}) => {
  return (
    <Box align="center" direction="row" gap={'12px'}>
      {check ? (
        <CheckIcon />
      ) : (
        <Box
          className={`h-5 w-5 rounded-full items-center justify-center text-xs ${bg ? 'bg-[#E1E1E1]' : 'text-[#A0A0A6] border border-[#A0A0A6] '}`}
        >
          {step}
        </Box>
      )}
      <p className="text-[#2F2F33] text-sm">{label}</p>
    </Box>
  )
}

type Step = {
  label: string
}

type FormStepsProps = {
  steps: Step[]
  currentStep: number // index (0-based)
}

export const FormStep2 = ({ currentStep, steps }: FormStepsProps) => {
  return (
    <div className="flex items-center gap-6">
      {steps?.map((step, index) => {
        const isCompleted = index < currentStep
        const isActive = index === currentStep

        return (
          <div className="flex items-center" key={index}>
            {/* Step Circle */}
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                isCompleted
                  ? 'bg-green-700 text-white'
                  : isActive
                    ? 'bg-gray-200 text-black ring-2 ring-green-700'
                    : 'bg-gray-200 text-gray-500'
              }`}
            >
              {isCompleted ? <Checkmark color="white" size="12px" /> : index + 1}
            </div>

            {/* Label */}
            <span className={`ml-2 text-sm ${isCompleted || isActive ? 'text-black' : 'text-gray-500'}`}>
              {step.label}
            </span>

            {/* Divider Line */}
            {index !== steps?.length - 1 && <div className="mx-4 w-10 h-px bg-gray-300"></div>}
          </div>
        )
      })}
    </div>
  )
}

export interface StepProps {
  id: string
  label: string
  completed?: boolean
  skipped?: boolean
}

interface FormStepperProps {
  steps: StepProps[]
  currentStep: string
  onStepClick?: (stepId: string) => void
}

export const FormStepper: React.FC<FormStepperProps> = ({ steps, currentStep, onStepClick }: FormStepperProps) => {
  return (
    <div className="flex items-center space-x-4">
      {steps?.map((step, index) => {
        const isActive = currentStep === step?.id
        const isCompleted = step.completed
        const isLast = index === steps?.length - 1

        return (
          <div className="flex items-center" key={step?.id}>
            <button
              className="flex items-center space-x-2 group cursor-pointer"
              disabled={!onStepClick}
              onClick={() => onStepClick?.(step?.id)}
              type="button"
            >
              <div
                className={classNames(
                  'flex items-center justify-center w-6 h-6 rounded-full border transition-all text-sm',
                  {
                    'bg-gray-200 border-gray-300 text-gray-700': !isCompleted,
                    'bg-green-600 border-green-600 text-white': isCompleted,
                    'ring-2 ring-green-700 text-black ': isActive
                  }
                )}
              >
                {isCompleted ? <Checkmark color="white" size="small" /> : <span className="">{index + 1}</span>}
              </div>
              <span className={`text-sm ${isCompleted || isActive ? 'text-black' : 'text-gray-500'}`}>
                {step.label}
              </span>
            </button>

            {!isLast && <div className="w-10 h-px bg-gray-300 mx-2 group-last:hidden" />}
          </div>
        )
      })}
    </div>
  )
}

export const Divider = ({ style }: { style?: CSSProperties }) => (
  <Box background="black" height="1px" style={{ opacity: 0.3, ...style }} width="100%" />
)
